import { useApiState } from 'hooks/useApiState';
import { useAppState } from 'hooks/useAppState';
import { useEffect, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useForm } from 'react-hook-form';
import type { UseFormReturn } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { AcctState } from 'stores/atoms/acctState';
import { submit } from 'utils/app';

// ------------------------------------------------------------------ //
// 型定義
// ------------------------------------------------------------------ //

export type SettingMemberEditLocationStateType = {
  companyMemberId: number;
  userName: string;
  userMailAddress: string;
  roleDiv: number;
};

export type SettingMemberEditFormStateType = {
  userName: string;
  roleDiv: number;
};

export type UseSettingMemberEditResult = {
  initialized: boolean;
  locationState: SettingMemberEditLocationStateType | null;
  processingOnSave: boolean;
  processingOnDelete: boolean;
  useFormMethods: UseFormReturn<SettingMemberEditFormStateType>;
  isLoginUser: boolean;
  onSave: () => void;
  onDelete: () => void;
  onBack: () => void;
};

// ------------------------------------------------------------------ //
// hook
// ------------------------------------------------------------------ //

/**
 * カンパニーメンバー詳細画面のロジックを集約するカスタムフック。
 */
export function useSettingMemberEdit(): UseSettingMemberEditResult {
  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const { showBoundary } = useErrorBoundary();
  const navigate = useNavigate();
  const location = useLocation();

  const [acctState] = useAtom(AcctState);

  const { updateAcctState, noticeMessageSuccess } = useAppState();
  const { postCompanyMemberUpdate, postCompanyMemberDelete } = useApiState();

  const [initialized, setInitialized] = useState(false);
  const [locationState, setLocationState] = useState<SettingMemberEditLocationStateType | null>(
    null
  );
  const [processingOnSave, setProcessingOnSave] = useState(false);
  const [processingOnDelete, setProcessingOnDelete] = useState(false);

  const useFormMethods = useForm<SettingMemberEditFormStateType>({
    defaultValues: {
      userName: '',
      roleDiv: undefined,
    },
  });
  const { setValue, handleSubmit } = useFormMethods;

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */
  useEffect(() => {
    const state = location.state as SettingMemberEditLocationStateType;

    if (!state) {
      return;
    }

    setValue('userName', state.userName);
    setValue('roleDiv', state.roleDiv);
    // eslint-disable-next-line react-hooks/set-state-in-effect -- 初回マウント時の初期化のため意図的
    setLocationState(state);
    setInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 初回マウント時のみ実行する設計
  }, []);

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * 本人かどうかを判定
   */
  const _isLoginUser = (): boolean => {
    return locationState?.companyMemberId === acctState.company.member.id;
  };

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * 保存ボタン押下時の処理
   */
  const onSave = () =>
    submit(
      processingOnSave,
      setProcessingOnSave,
      handleSubmit(async (data: SettingMemberEditFormStateType) => {
        try {
          await postCompanyMemberUpdate({
            companyMemberId: locationState!.companyMemberId,
            userName: data.userName,
            roleDiv: data.roleDiv,
          });

          if (_isLoginUser()) {
            await updateAcctState(['user']);
          }

          noticeMessageSuccess('メンバー情報の保存が完了しました。');
          navigate(`/setting/${acctState.company.cd}/member`);
        } catch (e) {
          showBoundary(e);
        }
      })
    );

  /**
   * 削除ボタン押下時の処理
   */
  const onDelete = () =>
    submit(processingOnDelete, setProcessingOnDelete, async () => {
      try {
        await postCompanyMemberDelete({
          companyMemberId: locationState!.companyMemberId,
        });

        noticeMessageSuccess('メンバー情報の保存が完了しました。');
        navigate(`/setting/${acctState.company.cd}/member`);
      } catch (e) {
        showBoundary(e);
      }
    });

  /**
   * 戻るリンク押下時の処理
   */
  const onBack = () => {
    navigate(-1);
  };

  return {
    initialized,
    locationState,
    processingOnSave,
    processingOnDelete,
    useFormMethods,
    isLoginUser: _isLoginUser(),
    onSave,
    onDelete,
    onBack,
  };
}
